// [C34] D-308 (implements D-307 (3)): the watchdog's explanations of a stored cash difference, shown to the approver
// inside the approve / reject dialogs of section 12 before deciding (new element — no old counterpart; owner sign-off
// on its look as D-064). Read-only: every line names its operations and shows its arithmetic; confidence in three
// grades, never a percentage (D-307 (2)). The container formats every figure and sentence parameter.
import { Calculator, ShieldCheck } from 'lucide-react';
import { useI18n } from '../i18n-context.js';

export type HypothesisGrade = 'high' | 'medium' | 'low';

export interface HypothesisItem {
  readonly rank: number;
  readonly grade: HypothesisGrade;
  /** i18n key of the sentence (`mt.hyp.*`) and its formatted parameters. */
  readonly sentenceKey: string;
  readonly params: Readonly<Record<string, string>>;
  /** The arithmetic, already formatted (e.g. "250,000 = 250,000"). */
  readonly arithmetic: string;
  readonly operations: ReadonlyArray<{ readonly id: string; readonly code: string; readonly label: string; readonly time: string; readonly cancelled: boolean }>;
}

export interface HypothesesState {
  /** pending = the watchdog has not analysed the count yet; none = analysed, nothing found; failed = the analysis failed. */
  readonly status: 'loading' | 'pending' | 'ready' | 'none' | 'failed';
  readonly items: readonly HypothesisItem[];
}

const GRADE_CLASS: Record<HypothesisGrade, string> = {
  high: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  low: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
};

export function DifferenceHypotheses({ state, onOpenOperation }: { readonly state: HypothesesState; readonly onOpenOperation?: (id: string) => void }) {
  const { t } = useI18n();
  return (
    <div className="rounded-2xl border border-indigo-500/20 bg-indigo-500/5 p-4 space-y-3 text-right" data-testid="difference-hypotheses">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-black text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5">
          <ShieldCheck size={14} />
          {t('mt.hyp.title')}
        </span>
        <span className="text-[9px] font-bold text-gray-400 dark:text-[#8b949e]">{t('mt.hyp.never_posts')}</span>
      </div>
      {state.status === 'loading' || state.status === 'pending' ? (
        <p className="text-[11px] font-bold text-gray-500 dark:text-[#8b949e]" data-testid="hyp-pending">
          {t(state.status === 'pending' ? 'mt.hyp.pending' : 'mt.hyp.loading')}
        </p>
      ) : state.status === 'failed' ? (
        <p className="text-[11px] font-bold text-rose-500">{t('mt.hyp.failed')}</p>
      ) : state.items.length === 0 ? (
        <p className="text-[11px] font-bold text-gray-500 dark:text-[#8b949e]" data-testid="hyp-none">
          {t('mt.hyp.none')}
        </p>
      ) : (
        <ol className="space-y-2 max-h-56 overflow-y-auto">
          {state.items.map((h) => (
            <li key={h.rank} className="rounded-xl bg-white dark:bg-[#0d1117] border border-gray-100 dark:border-[#30363d] p-3 space-y-1.5" data-testid="hyp-item">
              <div className="flex items-start justify-between gap-2">
                <p className="text-[11px] font-bold text-gray-800 dark:text-gray-200 leading-relaxed">
                  <span className="font-mono text-gray-400 ms-1">{h.rank}.</span> {t(h.sentenceKey, h.params)}
                </p>
                <span className={`shrink-0 px-2 py-0.5 rounded-full text-[9px] font-black ${GRADE_CLASS[h.grade]}`} data-testid={`hyp-grade-${h.grade}`}>
                  {t(`mt.hyp.grade_${h.grade}`)}
                </span>
              </div>
              <p className="text-[10px] font-mono font-bold text-indigo-600 dark:text-indigo-400 flex items-center gap-1" dir="ltr">
                <Calculator size={11} />
                {h.arithmetic}
              </p>
              {h.operations.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {h.operations.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      disabled={!onOpenOperation}
                      onClick={() => onOpenOperation?.(o.id)}
                      className={`px-2 py-0.5 rounded-lg text-[9px] font-bold border ${o.cancelled ? 'line-through text-gray-400 border-gray-200 dark:border-[#30363d]' : 'text-gray-600 dark:text-gray-300 border-gray-200 dark:border-[#30363d] hover:bg-gray-50 dark:hover:bg-[#161b22]'}`}
                    >
                      #{o.code} · {o.label} · {o.time}
                    </button>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
